import axios from 'axios'

/** Normalize FastAPI `detail` (string, array, or object) to user-visible text. */
export function formatApiDetail(detail: unknown): string {
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim()
  }

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') {
          const row = item as { msg?: string; message?: string; loc?: unknown[] }
          const msg =
            typeof row.msg === 'string' && row.msg.trim()
              ? row.msg.trim()
              : typeof row.message === 'string' && row.message.trim()
                ? row.message.trim()
                : ''
          if (!msg) return ''
          const loc = Array.isArray(row.loc)
            ? row.loc.filter((p) => p !== 'body' && typeof p === 'string').join('.')
            : ''
          return loc ? `${loc}: ${msg}` : msg
        }
        return ''
      })
      .filter(Boolean)
    if (parts.length) return parts.join(' ')
  }

  if (detail && typeof detail === 'object') {
    const obj = detail as Record<string, unknown>
    if (typeof obj.message === 'string' && obj.message.trim()) {
      return obj.message.trim()
    }
    if (typeof obj.error === 'string' && obj.error.trim()) {
      return obj.error.trim()
    }
    if (obj.requires_captcha) {
      return 'Please complete the CAPTCHA verification and try again.'
    }
    if (typeof obj.retry_after === 'number' && obj.retry_after > 0) {
      const minutes = Math.max(1, Math.ceil(obj.retry_after / 60))
      return `Too many login attempts. Please wait about ${minutes} minute(s) and try again.`
    }
  }

  return ''
}

/** FastAPI handlers in this project may return `detail` or wrap it as `error`. */
export function getApiErrorPayload(data: unknown): unknown {
  if (!data || typeof data !== 'object') return data
  const body = data as Record<string, unknown>
  if ('detail' in body) return body.detail
  if ('error' in body) return body.error
  return data
}

/** Extract a user-visible message from an API error response. */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = getApiErrorPayload(err.response?.data)
    const fromDetail = formatApiDetail(detail)
    if (fromDetail) return fromDetail

    const message = err.response?.data?.message
    if (typeof message === 'string' && message.trim()) return message.trim()

    const errorField = err.response?.data?.error
    if (typeof errorField === 'string' && errorField.trim()) return errorField.trim()

    if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
      return 'Request timed out. Please try again.'
    }
    if (!err.response) {
      return 'Network error. Check your connection and try again.'
    }
    const status = err.response.status
    if (status === 401) {
      return 'Invalid email or password. Please check your credentials and try again.'
    }
    if (status === 502) {
      return 'AI could not complete the request. Try again later.'
    }
    if (status === 503) {
      return 'Service is not configured. Please contact your administrator.'
    }
    return `${fallback} (HTTP ${status})`
  }

  if (err instanceof Error && err.message) {
    if (err.message.includes('timeout')) {
      return 'Request timed out. Please try again.'
    }
    if (!err.message.startsWith('Request failed with status code')) {
      return err.message
    }
  }

  return fallback
}
